'use client';

import { EQueryKeys } from '@/lib/enums';
import { PageResponse } from '@/lib/types';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import useAxios from './useAxios';

export const usePages = ({
  pageId,
  pageSlug,
  portalId,
  initialPage,
  initialPageLocale,
}: {
  pageId?: string;
  pageSlug?: string;
  portalId?: string;
  /** The page as the server already fetched it for this slug; seeds the query so the client renders it without refetching. */
  initialPage?: PageResponse;
  /** The locale the server fetched `initialPage` in. */
  initialPageLocale?: string;
}) => {
  const { get } = useAxios();
  const { removeQueries } = useQueryClient();

  const { playerData, currentLanguage } = useAppState();
  const playerId = playerData?.playerId;

  const getPages = useQuery<PageResponse[], Error>({
    queryKey: [EQueryKeys.PAGES, playerId, portalId, currentLanguage],
    queryFn: async () => {
      if (!playerId || !portalId) {
        return [];
      }

      return get(`/game-portal/${portalId}/pages`, false, undefined, {}, true)
        .then((res) => {
          return res.pages;
        })
        .catch((err) => {
          console.error('Pages fetch failed:', err);
          throw err;
        });
    },
    enabled: !!portalId && !!currentLanguage,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
  });

  const removePages = () => removeQueries({ queryKey: [EQueryKeys.PAGES] });

  const getSinglePageById = useQuery<PageResponse, Error>({
    queryKey: [EQueryKeys.PAGES, `id_${pageId}`, portalId, currentLanguage, playerId],
    queryFn: async () => {
      return get(`/game-portal/${portalId}/pages/${pageId}`, false, undefined, {}, true).catch(
        (err) => {
          throw err;
        }
      );
    },
    enabled: !!pageId && !!portalId && !!currentLanguage,
  });

  const getSinglePageBySlug = useQuery<PageResponse, Error>({
    queryKey: [EQueryKeys.PAGES, `slug_${pageSlug}`, portalId, pageSlug, currentLanguage, playerId],
    queryFn: async () => {
      return get(`/game-portal/${portalId}/pages/slug/${pageSlug}`, false, undefined, {}, true)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    enabled: !!portalId && !!pageSlug && !!currentLanguage,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    // The server fetched anonymously, in its own locale. A logged-in player's
    // page is personalised by the API, and a language switch changes this key
    // before the refreshed server page arrives; both are fetched as before.
    initialData: !playerId && currentLanguage === initialPageLocale ? initialPage : undefined,
  });

  return {
    getPages,
    getSinglePageById,
    getSinglePageBySlug,
    removePages,
  };
};